"use client";

import { useEffect } from "react";

function isMasked(value: unknown): boolean {
  return typeof value === "string" && value.startsWith("webkit-masked-url://hidden/");
}

function hasMaskedStack(value: unknown): boolean {
  return Boolean(
    value &&
      typeof value === "object" &&
      "stack" in value &&
      typeof (value as { stack?: unknown }).stack === "string" &&
      (value as { stack: string }).stack.includes("webkit-masked-url://hidden/"),
  );
}

function isBodyAppendError(value: unknown): boolean {
  if (!value) return false;
  const message = typeof value === "string" ? value : typeof (value as { message?: unknown }).message === "string" ? (value as { message: string }).message : "";
  const stack = typeof value === "object" && typeof (value as { stack?: unknown }).stack === "string" ? (value as { stack: string }).stack : "";
  const combined = `${message} ${stack}`;
  return combined.includes("document.body") || combined.includes("body.appendChild") || message.includes("null is not an object");
}

export function MaskedUrlErrorGuard() {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      if (isBodyAppendError(event.error) || isBodyAppendError(event.message)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      const undefinedBrowserError =
        event.message === "undefined" &&
        (!event.error || hasMaskedStack(event.error)) &&
        (!event.filename || isMasked(event.filename));

      if (undefinedBrowserError || (isMasked(event.filename) && (!event.error || hasMaskedStack(event.error)))) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      if (reason == null || isMasked(reason) || hasMaskedStack(reason) || isBodyAppendError(reason) || isBodyAppendError(String(reason))) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };

    window.addEventListener("error", handleError, true);
    window.addEventListener("unhandledrejection", handleRejection, true);
    return () => {
      window.removeEventListener("error", handleError, true);
      window.removeEventListener("unhandledrejection", handleRejection, true);
    };
  }, []);

  return null;
}
